import { JSX, useContext, useEffect, useState } from "react";
import { isEqual } from "lodash";
import { FeatureFlagContext, Features } from "@app/feature-flags";
import { CountyProjectContext } from "@app/projects";
import { ClaimsActivityLineOfBusinessFilter, ClaimsActivityLineOfBusinessTypeEnum, ClaimsActivityVolumeLabelEnum } from "@app/projects/network-assessment/pci-dashboard/claims-activity";
import { EffectivenessEfficiencyFilter, EffectivenessEfficiencyValueEnum, GainsPercentFilter, VolumeLabelFilter, LocationConfidenceFilter, MotiveAppropriatenessFilter, MotiveScoreValueEnum, NewPatientClaimsEnum, NewPatientClaimsFilter } from "@app/projects/servicing-provider";
import { GainsPercentEnum } from "@app/projects/servicing-provider/types";
import { DropdownItem } from "@app/shared/checkbox-dropdown";
import { QESButton, QESTextInput } from "@app/shared/qes-standards";
import { TableType } from "@app/viewable-fields";
import { LocationConfidenceScoresLabelEnum } from "@app/projects/enumerators";
import { ZipProjectContext } from "@app/projects/zip-projects/ZipProjectContext";

interface ProviderClaimsFiltersProps {
  clearFilters?: boolean;
  handleProviderSearch: (
    searchValue: string,
    selectedEffectivenessValues?: EffectivenessEfficiencyValueEnum[],
    selectedEfficiencyValues?: EffectivenessEfficiencyValueEnum[],
    selectedVolumeLabels?: ClaimsActivityVolumeLabelEnum[],
    selectedLineOfBusiness?: ClaimsActivityLineOfBusinessTypeEnum,
    selectedLocationConfidenceValues?: LocationConfidenceScoresLabelEnum[],
    selectedMotiveAppropriatenessValues?: MotiveScoreValueEnum[],
    gainsPercent?: GainsPercentEnum,
    selectedNewPatientClaims?: NewPatientClaimsEnum[]
  ) => void;
  lastSearchValue: string;
  lastSelectedEffectivenessValues: EffectivenessEfficiencyValueEnum[];
  lastSelectedEfficiencyValues: EffectivenessEfficiencyValueEnum[];
  lastSelectedGainsPercent?: GainsPercentEnum;
  lastSelectedLineOfBusiness?: ClaimsActivityLineOfBusinessTypeEnum,
  lastSelectedVolumeLabels: ClaimsActivityVolumeLabelEnum[];
  lastSelectedLocationConfidenceValues?: LocationConfidenceScoresLabelEnum[];
  lastSelectedMotiveAppropriatenessValues?: MotiveScoreValueEnum[];
  lastSelectedNewPatientClaimsValues?: NewPatientClaimsEnum[];
  showGainsPercent?: boolean;
  tableType: TableType;
  updateChangeTracker: () => void;
  validLoadStatus: boolean;
  showLocationConfidence?: boolean;
  showNewPatientClaims?: boolean;
}

const ProviderClaimsFilters = (props: ProviderClaimsFiltersProps): JSX.Element => {
  const [gainsPercent, setGainsPercent] = useState<GainsPercentEnum>(null);
  const [searchValue, setSearchValue] = useState<string>("");
  const [selectedEffectivenessValues, setSelectedEffectivenessValues] = useState<DropdownItem<EffectivenessEfficiencyValueEnum>[]>([]);
  const [selectedEfficiencyValues, setSelectedEfficiencyValues] = useState<DropdownItem<EffectivenessEfficiencyValueEnum>[]>([]);
  const [selectedVolumeLabels, setSelectedVolumeLabels] = useState<DropdownItem<ClaimsActivityVolumeLabelEnum>[]>([]);
  const [selectedLineOfBusiness, setSelectedLineOfBusiness] = useState<ClaimsActivityLineOfBusinessTypeEnum>(ClaimsActivityLineOfBusinessTypeEnum.Total);
  const [selectedLocationConfidenceValues, setSelectedLocationConfidenceValues] = useState<DropdownItem<LocationConfidenceScoresLabelEnum>[]>([]);
  const [selectedMotiveAppropriatenessValues, setSelectedMotiveAppropriatenessValues] = useState<DropdownItem<MotiveScoreValueEnum>[]>([]);
  const [showMotiveAppropriateness, setShowMotiveAppropriateness] = useState<boolean>(false);
  const [selectedNewPatientClaimsValues, setSelectedNewPatientClaimsValues] = useState<DropdownItem<NewPatientClaimsEnum>[]>([]);

  const featureFlagContext = useContext(FeatureFlagContext);
  const countyContext = useContext(CountyProjectContext);
  const zipContext = useContext(ZipProjectContext);
  // zip is first because it has defaults that we're okay with countyContext overwriting if it exists
  // in a zip project countyContext is null so it doesn't matter
  const projectContext = { ...zipContext.state, ...countyContext, };

  useEffect(() => {
    if (!showMotiveAppropriateness &&
      projectContext.project.showMotiveAppropriatenessScore &&
      projectContext.runConfiguration.showMotiveAppropriatenessScore
    ) {
      setShowMotiveAppropriateness(true);
    }
  }, [featureFlagContext?.enabledFeatureFlags, projectContext?.project, projectContext?.runConfiguration]);

  const mappedVolumeLabels: DropdownItem<ClaimsActivityVolumeLabelEnum>[] = Object.values(ClaimsActivityVolumeLabelEnum).filter(x => x !== ClaimsActivityVolumeLabelEnum.Total).map(x => {
    return { key: x, name: x }
  });

  const mappedEffectivenessAndEfficiencyValues: DropdownItem<EffectivenessEfficiencyValueEnum>[] = Object.values(EffectivenessEfficiencyValueEnum).map(x => {
    return { key: x, name: x }
  });

  const mappedMotiveScoreValues: DropdownItem<MotiveScoreValueEnum>[] = Object.values(MotiveScoreValueEnum).map(x => {
    return { key: x, name: x }
  });

  const mappedNewPatientClaimsValues: DropdownItem<NewPatientClaimsEnum>[] = Object.values(NewPatientClaimsEnum).map(x => {
    return { key: x, name: x }
  });

  const mappedLocationConfidenceValues: DropdownItem<LocationConfidenceScoresLabelEnum>[] = Object.values(LocationConfidenceScoresLabelEnum).map(x => {
    let name = x.toString();
    if (x === LocationConfidenceScoresLabelEnum.VeryLow)
      name = "Very Low";
    if (x === LocationConfidenceScoresLabelEnum.Unknown)
      name = "No Score Available";
    return { key: x, name }
  });

  useEffect(() => {
    if (props.showGainsPercent) {
      setGainsPercent(GainsPercentEnum.All);
    }
  }, []);

  useEffect(() => {
    if (props.clearFilters) {
      onClearFiltersClick();
      props.updateChangeTracker();
    }
  }, [props.clearFilters]);

  const applyDisabled = (): boolean => {
    const volumeLabelsEqual = isEqual(
      props.lastSelectedVolumeLabels?.sort((a, b) => a.localeCompare(b)),
      selectedVolumeLabels.map(x => x.key).sort((a, b) => a.localeCompare(b))
    );

    const effectivenessValuesEqual = isEqual(
      props.lastSelectedEffectivenessValues?.sort((a, b) => a.localeCompare(b)),
      selectedEffectivenessValues.map(x => x.key).sort((a, b) => a.localeCompare(b))
    );

    const efficiencyValuesEqual = isEqual(
      props.lastSelectedEfficiencyValues?.sort((a, b) => a.localeCompare(b)),
      selectedEfficiencyValues.map(x => x.key).sort((a, b) => a.localeCompare(b))
    );

    const locationConfidenceValuesEqual = isEqual(
      props.lastSelectedLocationConfidenceValues?.sort((a, b) => a.localeCompare(b)),
      selectedLocationConfidenceValues.map(x => x.key).sort((a, b) => a.localeCompare(b))
    );

    const motiveAppropriatenessValuesEqual = isEqual(
      props.lastSelectedMotiveAppropriatenessValues?.sort((a, b) => a.localeCompare(b)),
      selectedMotiveAppropriatenessValues.map(x => x.key).sort((a, b) => a.localeCompare(b))
    );

    const newPatientClaimsValuesEqual = isEqual(
      props.lastSelectedNewPatientClaimsValues?.sort((a, b) => a.localeCompare(b)),
      selectedNewPatientClaimsValues.map(x => x.key).sort((a, b) => a.localeCompare(b))
    );

    if (props.validLoadStatus &&
      (props.showGainsPercent && !!gainsPercent || !props.showGainsPercent) &&
      selectedVolumeLabels.length > 0 &&
      selectedEffectivenessValues.length > 0 &&
      selectedEfficiencyValues.length > 0 &&
      ((props.showLocationConfidence && selectedLocationConfidenceValues.length > 0) || !props.showLocationConfidence) &&
      ((showMotiveAppropriateness && selectedMotiveAppropriatenessValues.length > 0) || !showMotiveAppropriateness) &&
      !!selectedLineOfBusiness && (
        !isEqual(props.lastSelectedGainsPercent, gainsPercent) ||
        !isEqual(props.lastSearchValue, searchValue) ||
        !volumeLabelsEqual ||
        !effectivenessValuesEqual ||
        !efficiencyValuesEqual ||
        !isEqual(props.lastSelectedLineOfBusiness, selectedLineOfBusiness) ||
        (props.showLocationConfidence && !locationConfidenceValuesEqual) ||
        (showMotiveAppropriateness && !motiveAppropriatenessValuesEqual) ||
        !newPatientClaimsValuesEqual
      )
    ) {
      return false;
    }

    return true;
  }

  const onClearFiltersClick = (): void => {
    if (props.showGainsPercent) {
      setGainsPercent(GainsPercentEnum.All);
    }

    setSearchValue("");
    setSelectedEffectivenessValues(mappedEffectivenessAndEfficiencyValues);
    setSelectedEfficiencyValues(mappedEffectivenessAndEfficiencyValues);
    setSelectedVolumeLabels(mappedVolumeLabels);
    setSelectedLineOfBusiness(ClaimsActivityLineOfBusinessTypeEnum.Total);
    setSelectedNewPatientClaimsValues(mappedNewPatientClaimsValues);

    if (props.showLocationConfidence) {
      setSelectedLocationConfidenceValues(mappedLocationConfidenceValues);
    }

    if (showMotiveAppropriateness) {
      setSelectedMotiveAppropriatenessValues(mappedMotiveScoreValues);
    }
  }

  const clearDisabled = (): boolean => {
    // restoring filters to their defaults is unnecessary if the currently-selected filters are already the defaults
    if (props.validLoadStatus && (
      (props.showGainsPercent && gainsPercent !== GainsPercentEnum.All) ||
      searchValue !== "" ||
      selectedEffectivenessValues.length !== mappedEffectivenessAndEfficiencyValues.length ||
      selectedEfficiencyValues.length !== mappedEffectivenessAndEfficiencyValues.length ||
      selectedVolumeLabels.length !== mappedVolumeLabels.length ||
      selectedLineOfBusiness !== ClaimsActivityLineOfBusinessTypeEnum.Total ||
      (props.showLocationConfidence && selectedLocationConfidenceValues.length !== mappedLocationConfidenceValues.length) ||
      (showMotiveAppropriateness && selectedMotiveAppropriatenessValues.length !== mappedEffectivenessAndEfficiencyValues.length) ||
      (selectedNewPatientClaimsValues.length !== mappedNewPatientClaimsValues.length)
    )) {
      return false;
    }

    return true;
  }

  const providerSearchColSize = (props.showGainsPercent && props.showNewPatientClaims) ? "8"
    : (props.showGainsPercent || props.showNewPatientClaims ? "10" : "12");

  return (
    <div className="row">
      <div className="col-12">
        <div className="card card-static">
          <div className="card-body">
            <div className="row">
              <div className="col-12">
                <label className="label-m mb-2">
                  Filter Providers
                </label>
              </div>
            </div>
            <div className="row">
              <div className="col-10">
                <div className="row">
                  <div className={`col-md-${providerSearchColSize}`}>
                    <QESTextInput
                      containerClassName="mb-3"
                      testId={`provider-claims-filters__input--search-${props.tableType}`}
                      label="Provider Search"
                      onChange={(e) => setSearchValue(e.target.value)}
                      value={searchValue || ""}
                    />
                  </div>
                  {featureFlagContext.enabledFeatureFlags.has(Features.PCI_DASHBOARD_NEW_PATIENT_CPT) && props.showNewPatientClaims && (
                    <div className={`col-md-2`}>
                      <NewPatientClaimsFilter
                        mappedValues={mappedNewPatientClaimsValues}
                        selectedValues={selectedNewPatientClaimsValues}
                        setSelectedValues={setSelectedNewPatientClaimsValues}
                        testId={`provider-claims-filters__multiselect--new-patient-claims-${props.tableType}`}
                        title="New Patient Claims"
                      />
                    </div>
                  )}
                  {props.showGainsPercent && (
                    <div className="col-md-2">
                      <GainsPercentFilter
                        gainsPercent={gainsPercent}
                        setGainsPercent={setGainsPercent}
                        testId={`provider-claims-filters__select--gains-percent-${props.tableType}`}
                      />
                    </div>
                  )}
                </div>
                <div className="row">
                  <div className={`col-md-${(props.showLocationConfidence && showMotiveAppropriateness) ? "2" : "3"}`}>
                    <ClaimsActivityLineOfBusinessFilter
                      selectedLineOfBusiness={selectedLineOfBusiness}
                      setSelectedLineOfBusiness={setSelectedLineOfBusiness}
                      testId={`provider-claims-filter__select--line-of-business-${props.tableType}`}
                      validationTooltipContent="Line of business that should be considered when identifying provider claims status"
                      project={projectContext.project}
                    />
                  </div>
                  <div className={`col-md-${(props.showLocationConfidence && showMotiveAppropriateness) ? "2" : "3"}`}>
                    <VolumeLabelFilter
                      mappedVolumeLabels={mappedVolumeLabels}
                      selectedVolumeLabels={selectedVolumeLabels}
                      setSelectedVolumeLabels={setSelectedVolumeLabels}
                      testId={`provider-claims-filters__multiselect--volume-label-${props.tableType}`}
                    />
                  </div>
                  <div className={`col-md-${(props.showLocationConfidence || showMotiveAppropriateness) ? "2" : "3"}`}>
                    <EffectivenessEfficiencyFilter
                      mappedValues={mappedEffectivenessAndEfficiencyValues}
                      selectedValues={selectedEffectivenessValues}
                      setSelectedValues={setSelectedEffectivenessValues}
                      testId={`provider-claims-filters__multiselect--effectiveness-${props.tableType}`}
                      title="Effectiveness"
                    />
                  </div>
                  <div className={`col-md-${(props.showLocationConfidence || showMotiveAppropriateness) ? "2" : "3"}`}>
                    <EffectivenessEfficiencyFilter
                      mappedValues={mappedEffectivenessAndEfficiencyValues}
                      selectedValues={selectedEfficiencyValues}
                      setSelectedValues={setSelectedEfficiencyValues}
                      testId={`provider-claims-filters__multiselect--efficiency-${props.tableType}`}
                      title="Efficiency"
                    />
                  </div>
                  {props.showLocationConfidence && (
                    <div className="col-md-2">
                      <LocationConfidenceFilter
                        mappedValues={mappedLocationConfidenceValues}
                        selectedValues={selectedLocationConfidenceValues}
                        setSelectedValues={setSelectedLocationConfidenceValues}
                        testId={`provider-claims-filters__multiselect--location-confidence-${props.tableType}`}
                        title="Location Confidence"
                      />
                    </div>
                  )}
                  {(showMotiveAppropriateness) && (
                    <div className="col-md-2">
                      <MotiveAppropriatenessFilter
                        mappedValues={mappedMotiveScoreValues}
                        selectedValues={selectedMotiveAppropriatenessValues}
                        setSelectedValues={setSelectedMotiveAppropriatenessValues}
                        testId={`provider-claims-filters__multiselect--motive-appropriateness-${props.tableType}`}
                      />
                    </div>
                  )}
                </div>
              </div>
              <div className="col-md-2 d-flex align-items-end justify-content-center">
                <div>
                  <QESButton
                    className="pb-0"
                    disabled={applyDisabled()}
                    qesTestId={`providers-claims-filters__button--apply-${props.tableType}`}
                    qesType="secondary"
                    onClick={() => props.handleProviderSearch(
                      searchValue,
                      selectedEffectivenessValues.map(x => x.key),
                      selectedEfficiencyValues.map(x => x.key),
                      selectedVolumeLabels.map(x => x.key),
                      selectedLineOfBusiness,
                      selectedLocationConfidenceValues.map(x => x.key),
                      selectedMotiveAppropriatenessValues.map(x => x.key),
                      gainsPercent,
                      selectedNewPatientClaimsValues.map(x => x.key)
                    )}
                  >
                    Apply
                  </QESButton>

                  <QESButton
                    className="pb-0"
                    disabled={clearDisabled()}
                    qesTestId={`providers-claims-filters__button--clear-${props.tableType}`}
                    qesType="secondary-danger"
                    onClick={() => onClearFiltersClick()}
                  >
                    Clear
                  </QESButton>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ProviderClaimsFilters;
